/**
 * Run log on the connection detail view, newest first as the backend returns it.
 */
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { EhrSyncResult, EhrSyncRun } from '@models/ehr-sync';

import { formatDate } from '@utils/format';

import { toneTextColor } from '../../components/tone';
import { resultMessages } from '../../messages';

const messages = defineMessages({
  title: { id: 'ehrSync.detail.history.title', defaultMessage: 'Sync history' },
  started: {
    id: 'ehrSync.detail.history.column.started',
    defaultMessage: 'Started',
  },
  trigger: {
    id: 'ehrSync.detail.history.column.trigger',
    defaultMessage: 'Trigger',
  },
  result: {
    id: 'ehrSync.detail.history.column.result',
    defaultMessage: 'Result',
  },
  duration: {
    id: 'ehrSync.detail.history.column.duration',
    defaultMessage: 'Duration',
  },
  records: {
    id: 'ehrSync.detail.history.column.records',
    defaultMessage: 'Records',
  },
  recordsWritten: {
    id: 'ehrSync.detail.history.recordsWritten',
    defaultMessage: '{written, number} written',
  },
  recordsFailed: {
    id: 'ehrSync.detail.history.recordsFailed',
    defaultMessage: '{failed, number} failed',
  },
  recordsWrittenFailed: {
    id: 'ehrSync.detail.history.recordsWrittenFailed',
    defaultMessage: '{written, number} written · {failed, number} failed',
  },
  loadOlder: {
    id: 'ehrSync.detail.history.loadOlder',
    defaultMessage: 'Load older runs',
  },
  details: {
    id: 'ehrSync.detail.history.column.details',
    defaultMessage: 'Details',
  },
  empty: {
    id: 'ehrSync.detail.history.empty',
    defaultMessage: 'No sync runs recorded yet.',
  },
});

/** Props for {@link SyncHistoryTable}. */
interface SyncHistoryTableProps {
  /** Runs to list, newest first. */
  history: EhrSyncRun[];
  /** Fetches the next page of older runs. The button renders only when set. */
  onLoadOlder?: () => void;
}

const RESULT_TONE: Record<EhrSyncResult, 'success' | 'warning' | 'error'> = {
  success: 'success',
  partial: 'warning',
  failed: 'error',
};

/**
 * Sync history card: a run table with an empty state and an optional
 * "Load older runs" control.
 *
 * @param props - see {@link SyncHistoryTableProps}
 */
export default function SyncHistoryTable({
  history,
  onLoadOlder,
}: SyncHistoryTableProps) {
  const { formatMessage } = useIntl();

  /**
   * Formats a run's record counts, e.g. "318 written · 12 failed".
   *
   * @param run - the run to describe
   * @returns the localized counts, or "-" when nothing is known
   */
  const recordsLabel = ({ writtenCount, failedCount }: EhrSyncRun) => {
    if (writtenCount === null || writtenCount === undefined) {
      return failedCount
        ? formatMessage(messages.recordsFailed, { failed: failedCount })
        : '-';
    }
    return failedCount
      ? formatMessage(messages.recordsWrittenFailed, {
          written: writtenCount,
          failed: failedCount,
        })
      : formatMessage(messages.recordsWritten, { written: writtenCount });
  };

  const headers = [
    messages.started,
    messages.trigger,
    messages.result,
    messages.duration,
    messages.records,
    messages.details,
  ];

  return (
    <Paper variant="outlined">
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {formatMessage(messages.title)}
      </Typography>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.id}>
                  <Typography
                    variant="overline"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {formatMessage(header)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length}>
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage(messages.empty)}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {history.map((run, index) => (
              <TableRow key={`${run.at}-${index}`}>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="text.primary"
                  >
                    {formatDate({ date: run.at, formatter: 'datetime' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {run.trigger}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={(theme) => ({
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: toneTextColor(theme, RESULT_TONE[run.result]),
                    })}
                  >
                    {formatMessage(resultMessages[run.result])}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {run.durationLabel || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {recordsLabel(run)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {run.detail || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {onLoadOlder ? (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="text" size="small" onClick={onLoadOlder}>
            {formatMessage(messages.loadOlder)}
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}
